const sampleStock = Object.freeze({
  aura: { '3x3': ['IN_STOCK', 0, 5], '3x4': ['IN_STOCK', 0, 14] },
  como: { '3x3': ['PREORDER', 28, 3], '3x4': ['IN_STOCK', 0, 12] },
  tora: {
    '4x4': ['IN_STOCK', 0, 3],
    '5x3': ['IN_STOCK', 0, 10],
    '5x4': ['IN_STOCK', 0, 5],
    '6x3': ['IN_STOCK', 0, 6],
    '6x4': ['IN_STOCK', 0, 8],
    '8x4': ['IN_STOCK', 0, 2]
  }
});

function sampleArrival(days) {
  const date = new Date();
  date.setUTCHours(12, 0, 0, 0);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function previewSampleStock(state, catalogue, previewEnabled) {
  if (!previewEnabled && !['127.0.0.1', 'localhost'].includes(location.hostname)) return null;
  const base = catalogue[`base-${state.size}`];
  const family = state.family || 'aura';
  const draftHandles = {
    aura: 'luxsol-aura-manual-pergola',
    como: 'luxsol-como-electric-pergola',
    tora: 'luxsol-tora-electric-rgb-pergola'
  };
  if (base?.handle !== draftHandles[family]) return null;
  let [status, days, capacity] = sampleStock[family]?.[state.size] || [];
  if (!status) return null;
  let arrivalDate = days ? sampleArrival(days) : null;
  const original = base.previewOriginal;
  if (original) {
    const promised = stockAtQuantity(original.promise);
    if (promised && ['IN_STOCK', 'PREORDER', 'SOLD_OUT'].includes(promised.state)) {
      status = promised.state;
      arrivalDate = promised.arrivalDate;
      capacity = availableCapacity(original.promise, arrivalDate);
      if (capacity === null && status === 'IN_STOCK') capacity = Number(original.quantity);
    } else {
      capacity = Number(original.quantity);
      status = original.available && capacity > 0 ? 'IN_STOCK' : 'SOLD_OUT';
      arrivalDate = null;
    }
  }
  if (previewEnabled && status === 'SOLD_OUT') { status = 'IN_STOCK'; arrivalDate = null; capacity = 5; }
  const scenario = previewEnabled ? new URLSearchParams(location.search).get('stock_demo') : null;
  if (scenario === 'preorder') {
    status = 'PREORDER';
    arrivalDate = sampleArrival(28);
    capacity = 3;
  } else if (scenario === 'sold-out') {
    status = 'SOLD_OUT';
    arrivalDate = null;
    capacity = 0;
  }
  return {
    state: status,
    arrivalDate,
    source: 'sample',
    quantityAvailable: status === 'SOLD_OUT' || !Number.isFinite(capacity) ? null : capacity,
    quantityScope: status === 'SOLD_OUT' || !Number.isFinite(capacity) ? null : 'size',
    depositEligible: false,
    unavailable: []
  };
}

export function stockAtQuantity(stock, quantity = 1) {
  if (!stock || typeof stock !== 'object') return null;
  const schedule = Array.isArray(stock.schedule) ? stock.schedule : [];
  if (schedule.length) {
    let capacity = 0;
    for (const tranche of schedule) {
      capacity += Math.max(0, Number(tranche.capacity) || 0);
      if (quantity <= capacity) {
        return {
          state: tranche.date ? 'PREORDER' : 'IN_STOCK',
          arrivalDate: tranche.date || null,
          customerCopy: stock.customerCopy || ''
        };
      }
    }
    return { state: 'SOLD_OUT', arrivalDate: null, customerCopy: '' };
  }
  return {
    state: String(stock.state || 'IN_STOCK'),
    arrivalDate: stock.arrivalDate || null,
    customerCopy: stock.customerCopy || ''
  };
}

export function selectedParts(state) {
  const parts = new Map([[`base-${state.size}`, 1]]);
  const [width, depth] = String(state.size).split('x').map(Number);
  const frontLength = state.size === '8x4' ? 4 : Math.max(width, depth);
  const sideLength = state.size === '8x4' ? 4 : Math.min(width, depth);
  for (const [side, type] of Object.entries(state.sides || {})) {
    if (type === 'open') continue;
    const length = /^(front|back)/.test(side) ? frontLength : sideLength;
    const key = `${type}-${length}`;
    parts.set(key, (parts.get(key) || 0) + 1);
  }
  if (state.heater) parts.set('heater', state.heaterQuantity || (state.family === 'tora' ? 2 : 1));
  return parts;
}

export function unavailableParts(state, catalogue) {
  return [...selectedParts(state)].flatMap(([key, quantity]) => {
    const item = catalogue[key];
    const stock = stockAtQuantity(item?.stock, quantity);
    return stock?.state === 'SOLD_OUT' ? [item?.title || key] : [];
  });
}

function currentStock(item, quantity) {
  if (!item) return null;
  const managed = stockAtQuantity(item.stock, quantity);
  if (managed && managed.state !== 'SERVICE') return { ...managed, source: 'app' };
  return item.available === false
    ? { state: 'SOLD_OUT', arrivalDate: null, customerCopy: '', source: 'shopify' }
    : null;
}

function availableCapacity(stock, arrivalDate) {
  if (!Array.isArray(stock?.schedule) || !stock.schedule.length) return null;
  let capacity = 0;
  for (const tranche of stock.schedule) {
    const amount = Number(tranche.capacity);
    if (!Number.isFinite(amount) || amount < 0) return null;
    if (!tranche.date || (arrivalDate && tranche.date <= arrivalDate)) capacity += amount;
  }
  return capacity;
}

function configurationStock(state, catalogue, previewEnabled) {
  const sample = previewSampleStock(state, catalogue, previewEnabled);
  if (sample) return sample;
  const selected = [...selectedParts(state)].map(([key, quantity]) => {
    const item = catalogue[key];
    return {
      key,
      item,
      quantity,
      stock: currentStock(item, quantity)
    };
  });
  const known = selected.filter(part => part.stock);
  if (!known.length) return null;
  const unavailable = known.filter(part => part.stock.state === 'SOLD_OUT');
  const preorders = known.filter(part => part.stock.state === 'PREORDER')
    .sort((a, b) => String(b.stock.arrivalDate || '').localeCompare(String(a.stock.arrivalDate || '')));
  const chosen = unavailable[0] || preorders[0] || known[0];
  const source = chosen.stock.source;
  const arrivalDate = chosen.stock.state === 'PREORDER' ? chosen.stock.arrivalDate : null;
  let quantityAvailable = null;
  let quantityScope = null;
  if (selected.every(part => part.stock?.source === 'app')) {
    const capacities = selected.map(part => {
      const capacity = availableCapacity(part.item?.stock, arrivalDate);
      return capacity === null ? null : Math.floor(capacity / part.quantity);
    });
    if (capacities.every(Number.isFinite)) {
      quantityAvailable = Math.min(...capacities);
      quantityScope = 'build';
    }
  }
  if (quantityAvailable === null && source === 'app' && chosen.stock.state !== 'SOLD_OUT') {
    const base = selected.find(part => part.key === `base-${state.size}`);
    if (base?.stock?.source === 'app' && base.stock.state === chosen.stock.state) {
      quantityAvailable = availableCapacity(base.item?.stock, arrivalDate);
      quantityScope = quantityAvailable === null ? null : 'size';
    }
  }
  return {
    ...chosen.stock,
    source,
    quantityAvailable,
    quantityScope,
    unavailable: unavailable.map(part => part.item?.title).filter(Boolean),
    depositEligible: selected.every(part => Boolean(part.item?.depositPlanId))
  };
}

function formattedDate(date) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(date || ''))) return '';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC'
  }).format(new Date(`${date}T12:00:00Z`));
}

function mountBuilderStock(builder) {
  if (builder.dataset.luxsolStockBridge) return;
  if (!builder.instance || !builder.shadowRoot) {
    requestAnimationFrame(() => mountBuilderStock(builder));
    return;
  }

  const config = JSON.parse(builder.querySelector('[data-aura-config]')?.textContent || '{}');
  const root = builder.shadowRoot;
  const intro = root.querySelector('.pdp-product-intro');
  const existing = intro?.querySelector('[data-base-availability]');
  const buildSummary = root.querySelector('.pdp-build-drawer .pdp-your-build');
  if (!intro || !existing || !config.catalogue || !buildSummary) return;
  builder.dataset.luxsolStockBridge = 'true';

  const style = document.createElement('style');
  style.textContent = `
    .pdp-availability[hidden]{display:none!important}
    .luxsol-builder-stock{--stock-accent:#26704c;--stock-ink:#185438;--stock-surface:#eaf5ed;--stock-border:#c6e1cd;display:grid;grid-template-rows:auto minmax(80px,auto) auto;overflow:hidden;margin-top:16px;border:1px solid #d9e0dc;border-radius:11px;background:#fff;color:#1c2924;box-shadow:0 6px 20px rgba(20,42,32,.045)}
    .luxsol-builder-stock[hidden]{display:none!important}
    .luxsol-builder-stock[data-stock-state="PREORDER"]{--stock-accent:#a4741e;--stock-ink:#765213;--stock-surface:#fff4df;--stock-border:#ecd8ae}
    .luxsol-builder-stock[data-stock-state="SOLD_OUT"]{--stock-accent:#a55246;--stock-ink:#813e35;--stock-surface:#fbefed;--stock-border:#edcbc5}
    .luxsol-builder-stock__header{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:15px 17px 0}
    .luxsol-builder-stock__label{color:#69746e;font-size:12px;font-weight:600;line-height:1.3}
    .luxsol-builder-stock__state{display:inline-flex;align-items:center;gap:7px;max-width:65%;padding:6px 10px;border:1px solid var(--stock-border);border-radius:999px;background:var(--stock-surface);color:var(--stock-ink);font-size:11px;font-weight:650;line-height:1.2;text-align:right}
    .luxsol-builder-stock__state-dot{width:7px;height:7px;flex:0 0 7px;border-radius:50%;background:var(--stock-accent)}
    .luxsol-builder-stock__main{display:flex;min-width:0;flex-direction:column;justify-content:center;padding:11px 17px 16px}
    .luxsol-builder-stock__title{display:block;font-size:21px;font-weight:500;letter-spacing:-.035em;line-height:1.2}
    .luxsol-builder-stock__context{display:block;margin-top:5px;color:#69746e;font-size:12px;line-height:1.4}
    .luxsol-builder-stock__quantity{display:flex;align-items:baseline;gap:6px;margin-top:13px;padding-top:10px;border-top:1px solid #e6ebe7;color:#46584d}
    .luxsol-builder-stock__quantity[hidden]{display:none!important}
    .luxsol-builder-stock__quantity-prefix{color:#1c2924;font-size:12px;font-weight:600;line-height:1.4}
    .luxsol-builder-stock__quantity-prefix[hidden]{display:none!important}
    .luxsol-builder-stock__quantity-number{color:#1c2924;font-size:16px;font-weight:600;letter-spacing:-.02em;line-height:1.2}
    .luxsol-builder-stock__quantity-copy{font-size:12px;line-height:1.4}
    .luxsol-builder-stock__footer{display:grid;grid-template-columns:minmax(0,.72fr) minmax(0,1.28fr);min-height:74px;padding:13px 17px 14px;border-top:1px solid #e6ebe7;background:#f7f9f7}
    .luxsol-builder-stock__footer-cell{display:flex;min-width:0;flex-direction:column;gap:4px;align-items:flex-start}
    .luxsol-builder-stock__footer-cell+.luxsol-builder-stock__footer-cell{padding-left:15px;border-left:1px solid #e0e6e1}
    .luxsol-builder-stock__footer-label{color:#718078;font-size:10px;font-weight:600;letter-spacing:.025em;line-height:1.3}
    .luxsol-builder-stock__selection{color:#26352c;font-size:13px;font-weight:600;line-height:1.35}
    .luxsol-builder-stock__detail{display:block;color:#45554b;font-size:12px;line-height:1.4}
    .luxsol-builder-stock__preview{grid-column:1/-1;display:flex;align-items:center;gap:12px;margin-top:12px;padding-top:12px;border-top:1px solid #e2e7e3;color:#45554b}
    .luxsol-builder-stock__preview[hidden]{display:none!important}
    .luxsol-builder-stock__preview-amount{flex:0 0 auto;padding-right:12px;border-right:1px solid #d7dfd9;color:#26352c;font-size:17px;font-weight:600;letter-spacing:-.03em;line-height:1.2}
    .luxsol-builder-stock__preview-copy{display:flex;min-width:0;flex-direction:column;gap:2px;font-size:11px;line-height:1.4}
    .luxsol-builder-stock__preview-copy strong{color:#26352c;font-size:12px;font-weight:600}
    .luxsol-builder-stock__heater-limit{margin:7px 0 0;color:#765213;font-size:12px;line-height:1.4}
    .luxsol-builder-stock__heater-limit[hidden]{display:none!important}
    @media(max-width:480px){.luxsol-builder-stock__header{padding:14px 14px 0}.luxsol-builder-stock__main{padding:11px 14px 14px}.luxsol-builder-stock__footer{padding:12px 14px 13px}.luxsol-builder-stock__footer-cell+.luxsol-builder-stock__footer-cell{padding-left:12px}.luxsol-builder-stock__title{font-size:19px}}
  `;
  root.append(style);

  const status = document.createElement('div');
  status.className = 'luxsol-builder-stock';
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  const header = document.createElement('div');
  header.className = 'luxsol-builder-stock__header';
  const label = document.createElement('span');
  label.className = 'luxsol-builder-stock__label';
  const stateBadge = document.createElement('span');
  stateBadge.className = 'luxsol-builder-stock__state';
  const stateDot = document.createElement('span');
  stateDot.className = 'luxsol-builder-stock__state-dot';
  stateDot.setAttribute('aria-hidden', 'true');
  const stateText = document.createElement('span');
  stateBadge.append(stateDot, stateText);
  header.append(label, stateBadge);
  const main = document.createElement('div');
  main.className = 'luxsol-builder-stock__main';
  const title = document.createElement('strong');
  title.className = 'luxsol-builder-stock__title';
  const context = document.createElement('span');
  context.className = 'luxsol-builder-stock__context';
  const quantityLine = document.createElement('div');
  quantityLine.className = 'luxsol-builder-stock__quantity';
  const quantityPrefix = document.createElement('span');
  quantityPrefix.className = 'luxsol-builder-stock__quantity-prefix';
  const quantityNumber = document.createElement('strong');
  quantityNumber.className = 'luxsol-builder-stock__quantity-number';
  const quantityCopy = document.createElement('span');
  quantityCopy.className = 'luxsol-builder-stock__quantity-copy';
  quantityLine.append(quantityPrefix, quantityNumber, quantityCopy);
  main.append(title, context, quantityLine);
  const footer = document.createElement('div');
  footer.className = 'luxsol-builder-stock__footer';
  const selectionCell = document.createElement('div');
  selectionCell.className = 'luxsol-builder-stock__footer-cell';
  const selectionLabel = document.createElement('span');
  selectionLabel.className = 'luxsol-builder-stock__footer-label';
  const selection = document.createElement('span');
  selection.className = 'luxsol-builder-stock__selection';
  selectionCell.append(selectionLabel, selection);
  const nextCell = document.createElement('div');
  nextCell.className = 'luxsol-builder-stock__footer-cell';
  const nextLabel = document.createElement('span');
  nextLabel.className = 'luxsol-builder-stock__footer-label';
  const detail = document.createElement('span');
  detail.className = 'luxsol-builder-stock__detail';
  nextCell.append(nextLabel, detail);
  const preview = document.createElement('div');
  preview.className = 'luxsol-builder-stock__preview';
  const previewAmount = document.createElement('strong');
  previewAmount.className = 'luxsol-builder-stock__preview-amount';
  const previewCopy = document.createElement('span');
  previewCopy.className = 'luxsol-builder-stock__preview-copy';
  const previewTitle = document.createElement('strong');
  const previewDetail = document.createElement('span');
  previewCopy.append(previewTitle, previewDetail);
  preview.append(previewAmount, previewCopy);
  footer.append(selectionCell, nextCell, preview);
  status.append(header, main, footer);
  buildSummary.before(status);

  const addHeater = root.querySelector('[data-heater-adjust="1"]');
  const heaterCard = addHeater?.closest('.heater-card');
  const heaterLimit = document.createElement('p');
  heaterLimit.className = 'luxsol-builder-stock__heater-limit';
  heaterLimit.hidden = true;
  heaterCard?.after(heaterLimit);

  const labels = { ...(config.stockLabels || {}), ...(config.stockDisplayLabels || {}) };
  selectionLabel.textContent = labels.selectedSize || 'Selected size';
  nextLabel.textContent = labels.nextStep || 'What happens next';
  previewAmount.textContent = labels.depositAmount;
  let frame = 0;
  const render = () => {
    frame = 0;
    const state = builder.instance?.getState?.();
    const item = config.catalogue[`base-${state?.size}`];
    const stock = state && configurationStock({ ...state, family: state.family || config.family }, config.catalogue, config.sampleStockPreview === true);
    const heaterCapacity = availableCapacity(config.catalogue.heater?.stock, '9999-12-31');
    if (addHeater && state && heaterCapacity !== null) {
      const atLimit = state.heaterQuantity >= heaterCapacity;
      addHeater.disabled = atLimit;
      addHeater.title = atLimit ? `Maximum currently available: ${heaterCapacity} heater${heaterCapacity === 1 ? '' : 's'}` : '';
      heaterLimit.hidden = !atLimit;
      heaterLimit.textContent = heaterCapacity === 0
        ? 'Infrared heaters are currently unavailable.'
        : state.heaterQuantity > heaterCapacity
          ? `Only ${heaterCapacity} heaters are currently available to order. Reduce the quantity.`
          : `All ${heaterCapacity} currently orderable heaters selected.`;
    }
    if (!stock) {
      status.hidden = true;
      existing.hidden = false;
      return;
    }
    status.hidden = false;
    existing.hidden = true;
    status.dataset.stockState = stock.state;
    status.dataset.stockSource = stock.source;
    status.dataset.variantId = item?.id || '';
    label.textContent = stock.source === 'sample' ? 'Preview stock' : labels.availability;
    const date = formattedDate(stock.arrivalDate);
    const size = String(state.size || '').replace('x', ' × ') + 'm';
    selection.textContent = size;
    stateText.textContent = stock.state === 'PREORDER' ? labels.preorder
      : stock.state === 'SOLD_OUT' ? labels.soldOut : labels.inStock;
    title.textContent = stock.state === 'PREORDER'
      ? date || labels.preorder
      : stock.state === 'SOLD_OUT' ? labels.soldOutHeadline || labels.soldOut
        : labels.inStockHeadline || labels.inStock;
    context.textContent = stock.state === 'PREORDER' ? labels.preorderContext
      : stock.state === 'SOLD_OUT'
        ? stock.unavailable.length
          ? `${stock.unavailable.join(', ')} unavailable in the selected quantity.`
          : labels.selectedUnavailable
        : labels.inStockContext;
    const hasQuantity = Number.isFinite(stock.quantityAvailable) && stock.quantityScope;
    quantityLine.hidden = !hasQuantity;
    if (hasQuantity) {
      quantityPrefix.hidden = stock.quantityAvailable < 1 || stock.quantityAvailable > 5;
      quantityPrefix.textContent = labels.quantityOnly;
      quantityNumber.textContent = String(stock.quantityAvailable);
      const period = stock.state === 'PREORDER' ? 'Preorder' : 'InStock';
      quantityCopy.textContent = stock.quantityScope === 'size'
          ? labels[`quantitySize${period}`]
          : stock.state === 'SOLD_OUT' ? labels.quantityBuildSoldOut : labels[`quantityBuild${period}`];
    }
    detail.textContent = stock.source === 'sample'
      ? stock.state === 'PREORDER' ? labels.preorderNext
        : stock.state === 'SOLD_OUT' ? labels.soldOutNext : labels.inStockNote || labels.ready
      : !item?.available ? labels.notOrderable
      : stock.state === 'PREORDER' ? labels.preorderNext
        : stock.state === 'SOLD_OUT' ? labels.soldOutNext : labels.inStockNote || labels.ready;
    previewTitle.textContent = labels.depositTitle;
    previewDetail.textContent = labels.deposit;
    preview.hidden = stock.state !== 'PREORDER' || !(stock.source === 'sample' || (item?.available && stock.depositEligible));
  };
  const scheduleRender = () => {
    if (!frame) frame = requestAnimationFrame(render);
  };
  const originalAddBuild = builder.addBuild.bind(builder);
  builder.addBuild = async (state, liveConfig) => {
    const unavailable = unavailableParts(state, liveConfig.catalogue);
    if (unavailable.length) throw Error(`${unavailable.join(', ')}: ${labels.soldOut}`);
    return originalAddBuild(state, liveConfig);
  };
  const observer = new MutationObserver(scheduleRender);
  observer.observe(intro, { attributes: true, attributeFilter: ['data-product-size'] });
  root.addEventListener('click', scheduleRender);
  root.addEventListener('change', scheduleRender);
  render();
}

if (typeof document !== 'undefined') {
  customElements.whenDefined('aura-builder').then(() => {
    document.querySelectorAll('aura-builder').forEach(mountBuilderStock);
    new MutationObserver(records => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches('aura-builder')) mountBuilderStock(node);
          node.querySelectorAll('aura-builder').forEach(mountBuilderStock);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  });
}
